import { t } from '@lingui/core/macro'
import { useMemo, useState } from 'react'
import { Tab, Tabs } from 'react-bootstrap'
import ECharts from '~/components/echarts'
import EchartsGrid from '~/components/echarts/echarts_grid'
import FootNote from '~/components/footnote'
import AWSRDSDashboardDailyFilters from '~/dashboard/aws_rds_dashboard_daily_filters'
import AWSRDSDashboardFilters from '~/dashboard/aws_rds_dashboard_filters'
import AWSRDSDashboardReservationsFilters from '~/dashboard/aws_rds_dashboard_reservations_filters'
import {
  awsMonthlyRDSFteVariabilityChartPath,
  awsMonthlyRDSMemoryVariabilityChartPath,
  awsMonthlyRDSOnDemandSpendByInstanceTypeChartPath,
  awsMonthlyRDSRunningByDbEngineChartPath,
  awsMonthlyRDSRunningByPurchaseTypeChartPath,
  awsMonthlyRDSSavingsPercentageChartPath,
  awsMonthlyUnitCostByDbEngineChartPath,
  awsRDSDailySpendByUsageTypeChartPath,
  awsRDSReservationsEndingEachMonthChartPath,
} from '~/routes'

export default function AWSRDSDashboardCharts({ organizationId: orgId }) {
  const [mainParams, setMainParams] = useState({})
  const [dailySpendParams, setDailySpendParams] = useState({})
  const [reservationsSpendParams, setReservationsSpendParams] = useState({})

  const [openedTabs, setOpenedTabs] = useState(['main-tab'])
  const [activeTab, setActiveTab] = useState('main-tab')

  const handleTabChange = (tabName) => {
    setActiveTab(tabName)

    // Lazy load charts
    if (!openedTabs.includes(tabName)) {
      setOpenedTabs((prevOpenedTabs) => [...prevOpenedTabs, tabName])
    }
  }

  const openedTab = (tabName) => {
    return openedTabs.includes(tabName)
  }

  // memoize URLs to prevent rerenders
  const awsMonthlyRDSRunningByDbEngineChartUrl = useMemo(() => awsMonthlyRDSRunningByDbEngineChartPath(orgId), [orgId])
  const awsMonthlyRDSRunningByPurchaseTypeChartUrl = useMemo(
    () => awsMonthlyRDSRunningByPurchaseTypeChartPath(orgId),
    [orgId],
  )
  const awsMonthlyRDSSavingsPercentageChartUrl = useMemo(() => awsMonthlyRDSSavingsPercentageChartPath(orgId), [orgId])
  const awsMonthlyUnitCostByDbEngine = useMemo(() => awsMonthlyUnitCostByDbEngineChartPath(orgId), [orgId])
  const awsRDSReservationsEndingEachMonthChartUrl = useMemo(
    () => awsRDSReservationsEndingEachMonthChartPath(orgId),
    [orgId],
  )
  const awsMonthlyRDSOnDemandSpendByInstanceTypeChartUrl = useMemo(
    () => awsMonthlyRDSOnDemandSpendByInstanceTypeChartPath(orgId),
    [orgId],
  )
  const awsMonthlyRDSMemoryVariabilityChartUrl = useMemo(() => awsMonthlyRDSMemoryVariabilityChartPath(orgId), [orgId])
  const awsMonthlyRDSFteVariabilityChartUrl = useMemo(() => awsMonthlyRDSFteVariabilityChartPath(orgId), [orgId])
  const awsRDSDailySpendByUsageTypeChartUrl = useMemo(() => awsRDSDailySpendByUsageTypeChartPath(orgId), [orgId])

  return (
    <div className='card-tabs'>
      <Tabs id='controlled-tabs' activeKey={activeTab} onSelect={(k) => handleTabChange(k)}>
        <Tab eventKey='main-tab' title={t`Main Spend`}>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AWSRDSDashboardFilters organizationId={orgId} setChartParams={setMainParams} />

                {openedTab('main-tab') && (
                  <div className='row gy-4'>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Monthly RDS cost by DB engine`}
                        chartInfoTitle='aws_rds_monthly_cost_by_db_engine'
                        url={awsMonthlyRDSRunningByDbEngineChartUrl}
                        params={mainParams}
                        showExportButton
                      />
                    </div>
                    <EchartsGrid
                      title={t`Monthly RDS cost by Purchase Type`}
                      chartInfoTitle='aws_rds_monthly_cost_by_purchase_type_grid'
                      url={awsMonthlyRDSRunningByPurchaseTypeChartUrl}
                      params={mainParams}
                      showExportButton
                    />
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`RDS Monthly Savings Percentage`}
                        chartInfoTitle='aws_rds_monthly_savings_percentage'
                        url={awsMonthlyRDSSavingsPercentageChartUrl}
                        params={mainParams}
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Monthly Unit Cost By DB Engine(cost per GB of RAM Hour)`}
                        chartInfoTitle='aws_rds_monthly_unit_cost_by_db_engine'
                        url={awsMonthlyUnitCostByDbEngine}
                        params={mainParams}
                        showExportButton
                      />
                    </div>
                    <EchartsGrid
                      title={t`On Demand Spend By Instance Type`}
                      chartInfoTitle='aws_rds_on_demand_spend_by_instance_type_grid'
                      url={awsMonthlyRDSOnDemandSpendByInstanceTypeChartUrl}
                      params={mainParams}
                      showExportButton
                    />

                    <div className='col-sm-12'>
                      <ECharts
                        title={t`Memory Variability by DB Engine`}
                        chartInfoTitle='aws_rds_memory_variability_by_db_engine'
                        url={awsMonthlyRDSMemoryVariabilityChartUrl}
                        params={mainParams}
                      />
                    </div>
                    <div className='col-sm-12'>
                      <ECharts
                        title={t`FTE Variability by DB Engine`}
                        chartInfoTitle='aws_rds_fte_variability_by_db_engine'
                        url={awsMonthlyRDSFteVariabilityChartUrl}
                        params={mainParams}
                      />
                    </div>
                  </div>
                )}
                <div className='row'>
                  <FootNote />
                </div>
              </div>
            </div>
          </div>
        </Tab>
        <Tab eventKey='daily-tab' title={t`Daily Spend`}>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AWSRDSDashboardDailyFilters organizationId={orgId} setChartParams={setDailySpendParams} />

                {openedTab('daily-tab') && (
                  <div className='row gy-4'>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Daily Spend RDS By Usage Type`}
                        chartInfoTitle='aws_rds_daily_spend_by_usage_type'
                        url={awsRDSDailySpendByUsageTypeChartUrl}
                        params={dailySpendParams}
                        showExportButton
                      />
                    </div>
                  </div>
                )}
                <div className='row'>
                  <FootNote />
                </div>
              </div>
            </div>
          </div>
        </Tab>
        <Tab eventKey='reservations-tab' title={t`Reservations Spend`}>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AWSRDSDashboardReservationsFilters
                  organizationId={orgId}
                  setChartParams={setReservationsSpendParams}
                />

                {openedTab('reservations-tab') && (
                  <div className='row gy-4'>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Value of RDS Reservations Ending in Each Month`}
                        chartInfoTitle='aws_rds_reservations_ending_each_month_value'
                        url={awsRDSReservationsEndingEachMonthChartUrl}
                        params={reservationsSpendParams}
                        showExportButton
                      />
                    </div>
                  </div>
                )}
                <div className='row'>
                  <FootNote />
                </div>
              </div>
            </div>
          </div>
        </Tab>
      </Tabs>
    </div>
  )
}
